import {confirmedSentMessageId} from '@lib/messarioAi/sentMessageId';
import {
  completeMessarioAiTrainingEvent,
  dismissMessarioAiTrainingEvent
} from '@lib/messarioAi/api';
import {
  clearMessarioAiPending,
  clearMessarioAiPendingIfMatch,
  getMessarioAiPending
} from '@lib/messarioAi/pendingDraft';
import {getMessarioAiSession} from '@lib/messarioAi/storage';
import {notifyMessarioAiTrainingDraftChanged} from '@lib/messarioAi/trainingDraftUi';
import {getMessarioTemplatesAccess} from '@lib/messarioTemplates/storage';
import {NULL_PEER_ID} from '@appManagers/constants';
import rootScope from '@lib/rootScope';

const COMPLETE_RETRY_BACKOFF_MS = [200, 500] as const;
const COMPLETE_MAX_ATTEMPTS = 1 + COMPLETE_RETRY_BACKOFF_MS.length;

export type MessarioAiCompletionTarget = {
  external_user_id: string,
  account_id: string,
  peer_id: string | number,
  training_event_id: number | null,
  draft_generate_id: number | null,
  generated_reply_text: string,
  access_token: string,
  panel_origin: string
};

export type MessarioAiUnresolvedCompletion = {
  training_event_id: number,
  draft_generate_id: number | null,
  account_id: string,
  peer_id: string | number,
  final_reply_text: string,
  sent_message_id: string,
  access_token: string,
  panel_origin: string
};

/** Telegram success but Panel /complete not yet confirmed (or failed after retries). */
const unresolvedCompletionsByEventId = new Map<number, MessarioAiUnresolvedCompletion>();

function currentExternalUserId(): string | undefined {
  if(!rootScope.myId || rootScope.myId === NULL_PEER_ID) {
    return undefined;
  }

  return String(rootScope.myId);
}

function panelOrigin(): string {
  return import.meta.env.VITE_MESSARIO_PANEL_ORIGIN || '';
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

async function completeMessarioAiTrainingEventWithRetries(
  token: string,
  origin: string,
  trainingEventId: number,
  payload: {final_reply_text: string, sent_message_id: string}
): Promise<void> {
  let lastError: unknown;
  for(let attempt = 0; attempt < COMPLETE_MAX_ATTEMPTS; attempt++) {
    try {
      await completeMessarioAiTrainingEvent(token, origin, trainingEventId, payload);
      return;
    } catch(err) {
      lastError = err;
      if(attempt < COMPLETE_RETRY_BACKOFF_MS.length) {
        await sleep(COMPLETE_RETRY_BACKOFF_MS[attempt]);
      }
    }
  }

  throw lastError;
}

function registerUnresolvedCompletion(entry: MessarioAiUnresolvedCompletion) {
  unresolvedCompletionsByEventId.set(entry.training_event_id, entry);
}

function removeUnresolvedCompletion(trainingEventId: number) {
  unresolvedCompletionsByEventId.delete(trainingEventId);
}

function listUnresolvedForAccountPeer(
  accountId: string,
  peerId: string | number
): MessarioAiUnresolvedCompletion[] {
  const peerKey = String(peerId);
  return [...unresolvedCompletionsByEventId.values()].filter(
    (entry) => entry.account_id === accountId && String(entry.peer_id) === peerKey
  );
}

/** Test helper — clears unresolved completion map. */
export function resetUnresolvedCompletionsForTests() {
  unresolvedCompletionsByEventId.clear();
}

export function listUnresolvedCompletionsForTests(): MessarioAiUnresolvedCompletion[] {
  return [...unresolvedCompletionsByEventId.values()];
}

/**
 * Capture immutable complete target from CURRENT pending + session/auth.
 * Call synchronously at Send start (before Telegram RPC settles / UI race with Generate).
 */
export function captureMessarioAiCompletionTarget(
  peerId: PeerId
): MessarioAiCompletionTarget | null {
  const userId = currentExternalUserId();
  if(!userId) {
    return null;
  }

  const session = getMessarioAiSession(userId);
  const access = getMessarioTemplatesAccess(userId);
  if(!session || !access?.token) {
    return null;
  }

  const pending = getMessarioAiPending(session.account_id, peerId);
  if(!pending) {
    return null;
  }

  return {
    external_user_id: userId,
    account_id: session.account_id,
    peer_id: peerId,
    training_event_id: pending.training_event_id,
    draft_generate_id: pending.draft_generate_id,
    generated_reply_text: String(pending.reply_text || ''),
    access_token: access.token,
    panel_origin: panelOrigin()
  };
}

export type MessarioAiSendSuccessArgs = {
  peerId: PeerId,
  finalReplyText: string,
  sentMessageId?: string | number | null
};

/**
 * Call only after the Telegram send Promise resolves (RPC success).
 * Completes the captured target — NOT whatever is currently in pending for account+peer.
 *
 * Clears pending only after Panel /complete succeeds AND pending still matches the target.
 * On /complete failure: leave unresolved map entry; leave pending if still the same draft.
 *
 * Kept outside button.ts so ChatInput can import hooks without pulling
 * confirmationPopup / Solid Button (circular TDZ with PopupPeer).
 */
export async function noteMessarioAiSendSuccess(
  args: MessarioAiSendSuccessArgs,
  target: MessarioAiCompletionTarget
): Promise<void> {
  if(target.training_event_id == null) {
    clearMessarioAiPendingIfMatch(target.account_id, target.peer_id, {
      training_event_id: target.training_event_id,
      draft_generate_id: target.draft_generate_id
    });
    notifyMessarioAiTrainingDraftChanged();
    return;
  }

  const payload: {final_reply_text: string, sent_message_id: string} = {
    sent_message_id: args.sentMessageId == null ? '' : String(args.sentMessageId),
    final_reply_text: String(args.finalReplyText || target.generated_reply_text || '').trim()
  };
  if(!payload.final_reply_text || !payload.sent_message_id) {
    // Cannot complete without text — leave pending for a later successful send.
    return;
  }

  if(args.sentMessageId != null && String(args.sentMessageId) !== '') {
    payload.sent_message_id = String(args.sentMessageId);
  }

  const unresolved: MessarioAiUnresolvedCompletion = {
    training_event_id: target.training_event_id,
    draft_generate_id: target.draft_generate_id,
    account_id: target.account_id,
    peer_id: target.peer_id,
    final_reply_text: payload.final_reply_text,
    sent_message_id: payload.sent_message_id,
    access_token: target.access_token,
    panel_origin: target.panel_origin
  };
  if(payload.sent_message_id) {
    unresolved.sent_message_id = payload.sent_message_id;
  }
  registerUnresolvedCompletion(unresolved);

  try {
    await completeMessarioAiTrainingEventWithRetries(
      target.access_token,
      target.panel_origin,
      target.training_event_id,
      payload
    );
  } catch(err) {
    console.warn('Messario AI training complete failed after retries', err);
    throw err;
  }

  removeUnresolvedCompletion(target.training_event_id);
  clearMessarioAiPendingIfMatch(target.account_id, target.peer_id, {
    training_event_id: target.training_event_id,
    draft_generate_id: target.draft_generate_id
  });
  notifyMessarioAiTrainingDraftChanged();
}

/**
 * Retry Panel /complete for unresolved confirmed sends on the same account+peer.
 * Does NOT resend via Telegram. Throws if any entry still fails after retries.
 */
export async function retryUnresolvedMessarioAiCompletionsForPeer(
  accountId: string,
  peerId: string | number
): Promise<void> {
  const items = listUnresolvedForAccountPeer(accountId, peerId);
  if(!items.length) {
    return;
  }

  let lastError: unknown;
  let failed = 0;

  for(const item of items) {
    const payload: {final_reply_text: string, sent_message_id: string} = {
      sent_message_id: item.sent_message_id,
      final_reply_text: item.final_reply_text
    };
    if(item.sent_message_id) {
      payload.sent_message_id = item.sent_message_id;
    }

    try {
      await completeMessarioAiTrainingEventWithRetries(
        item.access_token,
        item.panel_origin,
        item.training_event_id,
        payload
      );
      removeUnresolvedCompletion(item.training_event_id);
      clearMessarioAiPendingIfMatch(item.account_id, item.peer_id, {
        training_event_id: item.training_event_id,
        draft_generate_id: item.draft_generate_id
      });
      notifyMessarioAiTrainingDraftChanged();
    } catch(err) {
      failed += 1;
      lastError = err;
      console.warn('Messario AI unresolved complete retry failed', err);
    }
  }

  if(failed > 0) {
    throw lastError instanceof Error ?
      lastError :
      new Error('Не удалось подтвердить предыдущую отправку Messario AI');
  }
}

/**
 * Fire-and-continue: capture target at Send start, wait for Telegram sendCompletion,
 * then note success → Panel /complete for THAT target.
 * On Telegram reject: do not complete and do not clear pending draft.
 * On /complete failure: unresolved stays; pending cleared only if still matching; warn and swallow.
 * Returns a Promise that settles after success/reject handling (for flag cleanup).
 */
export function watchMessarioAiSendCompletion(
  sendCompletion: Promise<unknown>,
  args: MessarioAiSendSuccessArgs
): Promise<void> {
  // Synchronous capture at Send start — identity must not follow later Generate B.
  const target = captureMessarioAiCompletionTarget(args.peerId);
  if(!target) {
    return sendCompletion.then(
      (): undefined => undefined,
      (): undefined => undefined
    );
  }

  return sendCompletion.then(
    (receipt): Promise<void> => noteMessarioAiSendSuccess({...args, sentMessageId: args.sentMessageId ?? confirmedSentMessageId(receipt)}, target),
    (): void => {
      // Telegram send failed — leave pending draft, skip Panel /complete.
    }
  ).then(
    (): undefined => undefined,
    (err: unknown): void => {
      console.warn('Messario AI sendCompletion note failed', err);
    }
  );
}

/**
 * Explicit composer clear / cancel of draft (not peer switch).
 */
export function noteMessarioAiDraftDismissed(peerId: PeerId) {
  const userId = currentExternalUserId();
  if(!userId) return;

  const session = getMessarioAiSession(userId);
  const access = getMessarioTemplatesAccess(userId);
  if(!session || !access?.token) return;

  const pending = getMessarioAiPending(session.account_id, peerId);
  if(!pending) return;

  clearMessarioAiPending(session.account_id, peerId);
  notifyMessarioAiTrainingDraftChanged();

  if(pending.training_event_id == null) {
    return;
  }

  void dismissMessarioAiTrainingEvent(
    access.token,
    panelOrigin(),
    pending.training_event_id
  );
}
